import { ClockCircleOutlined, GlobalOutlined } from "@ant-design/icons"
import { Badge, Space, Tooltip, Typography } from "antd"
import React, { useEffect, useState } from "react"

const { Text } = Typography

const formatTimezoneDisplay = (timezone: string): string => {
  if (!timezone) return ""

  try {
    // Get current time in that timezone
    const now = new Date()
    const options: Intl.DateTimeFormatOptions = {
      timeZone: timezone,
      hour12: false,
      hour: "2-digit",
      minute: "2-digit"
    }
    const time = new Intl.DateTimeFormat("en-US", options).format(now)

    // Get offset
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short"
    })
    const parts = formatter.formatToParts(now)
    const tzAbbr =
      parts.find((part) => part.type === "timeZoneName")?.value || ""

    // Extract city name from timezone (e.g., "Europe/Kyiv" -> "Kyiv")
    const city = timezone.split("/").pop()?.replace(/_/g, " ") || timezone

    // "UTC" is its own abbreviation, and so are the fixed-offset zones, which
    // would otherwise read "UTC (UTC)" / "GMT+2 (GMT+2)".
    if (!tzAbbr || tzAbbr === city) return city

    return `${city} (${tzAbbr})`
  } catch {
    return timezone
  }
}

export const TimezoneDisplay: React.FC<{
  timezone?: string
  variant?: "simple" | "detailed" | "time"
}> = ({ timezone, variant = "simple" }) => {
  const [currentTime, setCurrentTime] = useState<string>("")

  useEffect(() => {
    if (!timezone) return

    const updateTime = () => {
      const now = new Date()
      const options: Intl.DateTimeFormatOptions = {
        timeZone: timezone,
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      }
      const time = new Intl.DateTimeFormat("en-US", options).format(now)
      setCurrentTime(time)
    }

    updateTime()
    const interval = setInterval(updateTime, 1000)

    return () => clearInterval(interval)
  }, [timezone])

  if (!timezone) return null

  const displayInfo = formatTimezoneDisplay(timezone)

  switch (variant) {
    case "detailed":
      return (
        <Tooltip placement={"bottomLeft"} title={`Timezone: ${timezone}`}>
          <Space size="small" className="text-sm">
            <GlobalOutlined className="text-gray-500" />
            <Text type="secondary">{displayInfo}</Text>
            <Badge
              status="processing"
              text={
                <Text strong className="font-mono">
                  {currentTime}
                </Text>
              }
            />
          </Space>
        </Tooltip>
      )

    case "time":
      return (
        <Tooltip
          placement={"bottomLeft"}
          title={`${timezone} - ${displayInfo}`}
        >
          <Space size="small" className="text-sm">
            <ClockCircleOutlined className="text-gray-500" />
            <Text className="font-mono">{currentTime}</Text>
            <Text type="secondary" className="hidden sm:inline">
              {displayInfo.split("(")[1]?.replace(")", "")}
            </Text>
          </Space>
        </Tooltip>
      )

    case "simple":
    default:
      return (
        <Tooltip
          placement={"bottomLeft"}
          title={`Current time: ${currentTime}`}
        >
          <Space size="small" className="text-xs sm:text-sm">
            <GlobalOutlined className="text-slate-500" />
            {/* slate-600 rather than AntD's secondary token, which is
                rgba(0,0,0,.45) — 4.0:1 on white, under AA. */}
            <Text className="hidden text-slate-600 sm:inline">
              {displayInfo}
            </Text>
            <Text className="text-slate-600 sm:hidden">
              {displayInfo.split("(")[1]?.replace(")", "")}
            </Text>
          </Space>
        </Tooltip>
      )
  }
}
